import type { LedgerEntry } from '@/types';

interface BalanceChartProps {
    entries: LedgerEntry[];
}

export function BalanceChart({ entries }: BalanceChartProps) {
    if (entries.length < 2) return null;

    // Entries come newest-first from API, reverse for chronological
    const chronological = [...entries].reverse();

    const balances = chronological.map((e) => parseFloat(e.balance_after));
    const minBal = Math.min(...balances);
    const maxBal = Math.max(...balances);
    const range = maxBal - minBal || 1;

    const width = 600;
    const height = 120;
    const padX = 0;
    const padY = 8;
    const chartW = width - padX * 2;
    const chartH = height - padY * 2;

    const points = balances.map((bal, i) => {
        const x = padX + (i / (balances.length - 1)) * chartW;
        const y = padY + chartH - ((bal - minBal) / range) * chartH;
        return { x, y, bal };
    });

    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    const areaPath = `${linePath} L ${points[points.length - 1].x} ${height} L ${points[0].x} ${height} Z`;

    return (
        <div className="w-full">
            <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
                <span>${maxBal.toFixed(0)}</span>
                <span className="text-slate-600">Balance Over Time</span>
                <span>${minBal.toFixed(0)}</span>
            </div>
            <svg
                viewBox={`0 0 ${width} ${height}`}
                className="w-full h-auto"
                preserveAspectRatio="none"
            >
                <defs>
                    <linearGradient id="balanceGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgb(239,68,68)" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="rgb(239,68,68)" stopOpacity="0.02" />
                    </linearGradient>
                </defs>
                <path d={areaPath} fill="url(#balanceGrad)" />
                <path d={linePath} fill="none" stroke="rgb(239,68,68)" strokeWidth="2" />
                {/* Start and end dots */}
                <circle cx={points[0].x} cy={points[0].y} r="3" fill="rgb(239,68,68)" />
                <circle
                    cx={points[points.length - 1].x}
                    cy={points[points.length - 1].y}
                    r="3"
                    fill="rgb(239,68,68)"
                />
            </svg>
            <div className="flex items-center justify-between text-[10px] text-slate-600 mt-1">
                <span>{new Date(chronological[0].effective_at).toLocaleDateString()}</span>
                <span>{new Date(chronological[chronological.length - 1].effective_at).toLocaleDateString()}</span>
            </div>
        </div>
    );
}
